import React, { useEffect, useState } from 'react';
import Navbar from '../components/storefront/Navbar';
import Footer from '../components/storefront/Footer';
import { useStore } from '../context/StoreContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiShoppingCart, FiMail, FiCheckCircle, FiClock } from 'react-icons/fi';
import supabase from '../supabase/supabase';
import SafeIcon from '../common/SafeIcon';

const Shop = () => {
  const { products, addToCart, loading, appSettings } = useStore();
  const [filter, setFilter] = useState('All');
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const navigate = useNavigate();

  // Coming soon state
  const isComingSoon = appSettings?.find(s => s.key === 'shop_coming_soon')?.value === true ||
                       appSettings?.find(s => s.key === 'shop_coming_soon')?.value === 'true';

  // Waitlist form state
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistStatus, setWaitlistStatus] = useState(null); // 'success' | 'error' | null
  const [waitlistLoading, setWaitlistLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    if (!waitlistEmail.trim()) return;
    setWaitlistLoading(true);
    setWaitlistStatus(null);
    try {
      const { error } = await supabase.from('waitlist').insert({ email: waitlistEmail.trim().toLowerCase() });
      if (error) {
        if (error.message.includes('duplicate') || error.code === '23505') {
          setWaitlistStatus({ type: 'error', message: 'This email is already on our waitlist!' });
        } else {
          throw error;
        }
      } else {
        setWaitlistStatus({ type: 'success', message: "You're on the list! We'll notify you when we launch." });
        setWaitlistEmail('');
      }
    } catch (err) {
      setWaitlistStatus({ type: 'error', message: 'Something went wrong. Please try again.' });
    } finally {
      setWaitlistLoading(false);
    }
  };

  // Coming Soon View
  if (isComingSoon) {
    return (
      <div className="bg-brand-sand min-h-screen pt-24 font-sans">
        <Navbar />
        <div className="max-w-4xl mx-auto px-6 py-16 md:py-24 text-center">
          {/* Animated Badge */}
          <div className="inline-flex items-center gap-2 bg-brand-gold/10 border border-brand-gold/20 text-brand-gold px-5 py-2 rounded-full text-xs font-bold uppercase tracking-widest mb-8">
            <SafeIcon icon={FiClock} size={14} />
            Coming Soon
          </div>

          <h1 className="text-4xl md:text-6xl font-serif text-brand-darkSage mb-6 leading-tight">
            Our Shop is<br />
            <span className="text-brand-gold">Almost Ready</span>
          </h1>

          <p className="text-brand-lightText text-lg md:text-xl max-w-2xl mx-auto mb-12 leading-relaxed">
            We're carefully curating the finest authentic Ceylon wellness products for you. 
            Join our waitlist to be the first to know when we launch and receive an exclusive early-bird discount.
          </p>

          {/* Waitlist Form */}
          <div className="max-w-md mx-auto">
            <form onSubmit={handleWaitlistSubmit} className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-lightText w-5 h-5" />
                <input
                  type="email"
                  required
                  value={waitlistEmail}
                  onChange={(e) => setWaitlistEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="w-full pl-12 pr-4 py-4 border border-brand-darkSage/20 rounded-2xl bg-white/70 backdrop-blur-sm text-brand-darkSage placeholder-brand-lightText outline-none focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/20 transition-all"
                />
              </div>
              <button
                type="submit"
                disabled={waitlistLoading}
                className="bg-brand-darkSage text-white px-8 py-4 rounded-2xl font-bold uppercase tracking-widest text-xs hover:bg-brand-gold transition-all disabled:opacity-50 whitespace-nowrap"
              >
                {waitlistLoading ? 'Joining...' : 'Join Waitlist'}
              </button>
            </form>

            {/* Status Messages */}
            {waitlistStatus && (
              <div className={`mt-4 p-4 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 ${
                waitlistStatus.type === 'success'
                  ? 'bg-green-50 text-green-700 border border-green-100'
                  : 'bg-red-50 text-red-600 border border-red-100'
              }`}>
                <SafeIcon icon={waitlistStatus.type === 'success' ? FiCheckCircle : FiClock} size={16} />
                {waitlistStatus.message}
              </div>
            )}

            <p className="text-xs text-brand-lightText/60 mt-4">
              No spam, ever. We'll only email you when the shop goes live.
            </p>
          </div>

          {/* Feature Preview */}
          <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: 'Authentic Ceylon', desc: 'Sustainably sourced directly from Sri Lanka' },
              { title: 'Premium Quality', desc: 'Hand-selected finest grades and purity' },
              { title: 'Early Bird Offer', desc: '10% off your first order when we launch' },
            ].map((item, i) => (
              <div key={i} className="bg-white/50 backdrop-blur-sm p-6 rounded-2xl border border-white shadow-sm">
                <h3 className="font-serif text-brand-darkSage font-bold text-lg mb-2">{item.title}</h3>
                <p className="text-sm text-brand-lightText">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Normal Shop View
  const categories = ['All', ...new Set(products.map(p => p.category))];

  const filteredProducts = products.filter(p => {
    const matchesCategory = filter === 'All' || p.category === filter;
    const matchesSearch = !searchQuery || 
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (p.category && p.category.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (p.description && p.description.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="bg-brand-sand min-h-screen pt-24 font-sans">
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Centered Heading and Subtext */}
        <div className="text-center mb-12 flex flex-col items-center justify-center w-full">
          <h1 className="text-4xl md:text-5xl font-serif text-brand-darkSage mb-4 text-center">
            {searchQuery ? `Results for "${searchQuery}"` : 'Shop Collection'}
          </h1>
          <p className="text-brand-lightText text-center max-w-md mx-auto">
            {searchQuery
              ? `${filteredProducts.length} product${filteredProducts.length !== 1 ? 's' : ''} found`
              : 'Discover our authentic Ceylon wellness products.'
            }
          </p>
          {searchQuery && (
            <button
              onClick={() => navigate('/shop')}
              className="mt-4 text-sm font-semibold text-brand-gold hover:text-brand-darkSage transition-colors"
            >
              Clear search & view all products
            </button>
          )}
        </div>

        {/* Categories */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map(cat => (
            <button 
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-6 py-2 rounded-full text-sm font-semibold transition-all ${filter === cat ? 'bg-brand-darkSage text-white' : 'bg-white text-brand-darkSage hover:bg-brand-gold/10'}`}
            >
              {cat}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20 text-brand-darkSage">Loading products...</div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-brand-lightText text-lg mb-4">No products found matching your criteria.</p>
            <button
              onClick={() => navigate('/shop')}
              className="bg-brand-darkSage text-white px-8 py-3 rounded-full text-sm font-semibold hover:bg-brand-gold transition-colors"
            >
              Browse All Products
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {filteredProducts.map(product => (
              <div key={product.id} className="bg-white/50 backdrop-blur-sm rounded-3xl p-6 border border-white shadow-sm hover:shadow-xl transition-all group flex flex-col justify-between">
                <div 
                  className="cursor-pointer mb-6 aspect-square w-full bg-brand-sand/50 rounded-2xl flex items-center justify-center p-4 overflow-hidden relative"
                  onClick={() => navigate(`/product/${product.id}`)}
                >
                  <img src={product.image} alt={product.name} className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500" />
                  {product.stock_level < 10 && (
                    <span className="absolute top-2 right-2 bg-red-100 text-red-600 text-xs font-bold px-2 py-1 rounded-full">
                      Low Stock
                    </span>
                  )}
                </div>
                
                <div>
                  <p className="text-xs text-brand-gold uppercase tracking-widest font-semibold mb-2">{product.category}</p>
                  <h3 className="font-serif text-lg text-brand-darkSage font-bold mb-2 line-clamp-2">{product.name}</h3>
                  <div className="flex justify-between items-center mt-4">
                    <span className="text-xl font-bold text-brand-darkSage">Rs. {product.price.toFixed(2)}</span>
                    <button 
                      onClick={() => addToCart(product)}
                      className="p-3 bg-brand-darkSage text-white rounded-full hover:bg-brand-gold transition-colors"
                      aria-label="Add to cart"
                    >
                      <FiShoppingCart />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Shop;